import React from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { numberWithComma, formatDateWithoutTime } from 'helpers/format'
import { Col, Image, Row, Space } from 'antd'
import { appConfig } from 'config'
import { ImageFallback } from 'helpers/constanst'
import PdfCompanyInfo from 'Pages/Purchase/components/PdfCompanyInfo'
import { CreditNotesData, CreditNotesItem, GrnData } from 'Store/grn/types'
import { PdfPrintFormat } from 'Components/form'

type Props = {
  creditNotesData?: CreditNotesData
  detail?: GrnData
}

const CnPDF: React.FunctionComponent<Props> = props => {
  const { detail, creditNotesData } = props

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const renderImage = (product_photo: string) => (
    <ThumbImage src={appConfig.assetCdnEndpoint + product_photo} fallback={ImageFallback} />
  )

  const renderInfoItem = (
    title: any,
    value: any,
    justify: 'start' | 'end' = 'start',
    colon: boolean = true,
  ) => {
    return (
      <Row gutter={[8, 8]} justify={justify}>
        <Col>
          <Space wrap={true}>
            <InfoTitle>
              {title} {colon && ':'} {value || '-'}
            </InfoTitle>
          </Space>
        </Col>
      </Row>
    )
  }

  const renderSupplierInfo = () => {
    const supplier = detail?.supplier
    return (
      <div>
        {renderInfoItem('ผู้จำหน่าย', supplier?.supplier_code)}
        {renderInfoItem('บริษัท', supplier?.name)}
        {renderInfoItem('ที่อยู่', supplier?.address)}
        {renderInfoItem('เบอร์โทรศัพท์', supplier?.mobile_no)}
        <Col>บริษัทขอลดหนี้/ส่งคืนสินค้าตามรายการต่อไปนี้ : </Col>
      </div>
    )
  }

  const renderPurchaseInfo = () => {
    return (
      <div>
        {renderInfoItem('เลขที่ใบลดหนี้', creditNotesData?.credit_note_no, 'end')}
        {renderInfoItem('วันที่', formatDateWithoutTime(creditNotesData?.created_at || ''), 'end')}
        {renderInfoItem('ใบรับสินค้า', detail?.grn_no, 'end')}
        {renderInfoItem('ผู้สร้างรายการ', creditNotesData?.username, 'end')}
      </div>
    )
  }

  const renderProductName = (item: any, record: any) => {
    const isSameTitle = record?.product_name == record?.product_option_name
    return (
      <>
        <RowTile>{record?.product_name}</RowTile>
        {!isSameTitle && <ProductDesc>{record?.product_option_name}</ProductDesc>}​
      </>
    )
  }

  const renderSummaryItem = (title: any, val: number) => {
    return (
      <Row justify="end">
        <Col>
          <InfoValue>{title}</InfoValue>
        </Col>
        <Col span={12}>
          <InfoValue>{renderInfoItem('', `${numberWithComma(val, true)} บาท`, 'end', false)} </InfoValue>
        </Col>
      </Row>
    )
  }

  const calculateDiffPrice = () => {
    return ((creditNotesData?.total_price || 0) * 100) / 107
  }

  const calculateVatPrice = () => {
    return calculateDiffPrice() * 0.07
  }

  const summaryFooter = (
    <Row justify="space-between">
      <Col>{renderInfoItem('เหตุผล', creditNotesData?.remark)}</Col>
      <Col span={16}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {renderSummaryItem('ราคารวม', creditNotesData?.total_price || 0)}
          {renderSummaryItem(
            'มูลค่าของสินค้าหรือบริการตามใบกำกับภาษีเดิม',
            detail?.total_original_recieve_price || 0,
          )}
          {renderSummaryItem('ผลต่าง', calculateDiffPrice())}
          {renderSummaryItem('จำนวนภาษีมูลค่าเพิ่ม 7.00%', calculateVatPrice())}
          {renderSummaryItem('จำนวนเงินรวมทั้งสิ้น', creditNotesData?.total_price || 0)}
        </Space>
      </Col>
    </Row>
  )

  const tableHeader = (
    <thead>
      <th>ลำดับ</th>
      <th style={{ width: '50px' }}>รูปภาพ</th>
      <th className="left">รหัสสินค้า</th>
      <th style={{ width: '20%' }} className="left">
        รายการ
      </th>
      <th className="left" style={{ width: '50px' }}>
        ยี่ห้อ
      </th>
      <th>หน่วย</th>
      <th className="right">ราคา/หน่วย</th>
      <th>จำนวน</th>
      <th>ราคารวมภาษี</th>
    </thead>
  )

  const tableBody = (
    <tbody>
      {creditNotesData?.credit_note_items &&
        creditNotesData?.credit_note_items.map((item: CreditNotesItem, index: number) => {
          const data = detail?.items?.find(grn => grn.id === item.grn_item_id)
          return (
            <tr key={index}>
              <td>{renderInfo(index + 1)}</td>
              <td>{renderImage(item.product_image)}</td>
              <td className="left">{renderInfo(item?.product_sku || '-')}</td>
              <td className="left">{renderProductName(item?.product_name, item)}</td>
              <td className="left">{renderInfo(item?.product_manufacturer || '-')}</td>
              <td>{renderInfo(data?.product_uom || '-')}</td>
              <td className="right">{renderInfo(numberWithComma(item?.price_per_unit, true))}</td>
              <td>{renderInfo(numberWithComma(item?.amount))}</td>
              <td className="right">{renderInfo(numberWithComma(item?.price, true))}</td>
            </tr>
          )
        })}
    </tbody>
  )

  return (
    <PdfPrintFormat
      companyInfo={<PdfCompanyInfo merchant={detail?.merchant} />}
      title={'ใบลดหนี้/ รับคืนสินค้า'}
      leftInfo={renderSupplierInfo()}
      rightInfo={renderPurchaseInfo()}
      footerSpan={9}
      summaryFooter={summaryFooter}
      signPosition={['ผู้รับคืน', 'ผู้ส่งสินค้า', 'ผู้รับมอบอำนาจ']}
      thead={tableHeader}
      tbody={tableBody}
    />
  )
}

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 40px;
  height: 40px;
  object-fit: contain;
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const InfoTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const InfoValue = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  margin-right: 10px;
`

export default CnPDF
